// Google Analytics 4 utility functions

// gtag.js is loaded and configured in public/index.html (consent defaults + config with
// send_page_view: false). The ID is public, so it is hard-coded to match index.html; the env var
// only overrides it (e.g. a test property). It used to come from the env var alone, which was never
// set on Vercel, so every custom event and SPA page view silently no-op'd in production.
export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-VM1XME37NQ';

// Storage keys for tracking
const UTM_STORAGE_KEY = 'utm_params';
const FIRST_VISIT_KEY = 'first_visit_timestamp';
const SESSION_COUNT_KEY = 'session_count';

// Check if GA is available
const isGAAvailable = () => {
  return typeof window !== 'undefined' &&
         window.gtag &&
         GA_MEASUREMENT_ID;
};

// ==========================================
// UTM & Traffic Source Tracking
// ==========================================

// Capture UTM parameters from URL on page load
export const captureUtmParams = () => {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content'),
    referrer: document.referrer || null,
    landing_page: window.location.pathname,
    captured_at: new Date().toISOString()
  };

  // Only store if we have at least one UTM param or a referrer
  const hasData = utmParams.utm_source || utmParams.referrer;
  if (hasData) {
    // Don't overwrite existing UTM params (first touch attribution)
    const existing = localStorage.getItem(UTM_STORAGE_KEY);
    if (!existing) {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
    }
  }

  return utmParams;
};

// Get stored UTM parameters
export const getStoredUtmParams = () => {
  if (typeof window === 'undefined') return {};
  const stored = localStorage.getItem(UTM_STORAGE_KEY);
  return stored ? JSON.parse(stored) : {};
};

// Get traffic source summary for events
const getTrafficSource = () => {
  const utm = getStoredUtmParams();
  return {
    traffic_source: utm.utm_source || 'direct',
    traffic_medium: utm.utm_medium || 'none',
    traffic_campaign: utm.utm_campaign || null,
    referrer: utm.referrer || null
  };
};

// ==========================================
// Session & User Type Tracking
// ==========================================

// Track first visit and session count
export const initSessionTracking = () => {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  const firstVisit = localStorage.getItem(FIRST_VISIT_KEY);

  if (!firstVisit) {
    // New visitor
    localStorage.setItem(FIRST_VISIT_KEY, now.toString());
    localStorage.setItem(SESSION_COUNT_KEY, '1');
  } else {
    // Returning visitor - increment session count
    const sessionCount = parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '1', 10);
    localStorage.setItem(SESSION_COUNT_KEY, (sessionCount + 1).toString());
  }
};

// Get user type (new vs returning)
const getUserType = () => {
  if (typeof window === 'undefined') return 'unknown';

  const sessionCount = parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '1', 10);
  const firstVisit = localStorage.getItem(FIRST_VISIT_KEY);

  if (!firstVisit || sessionCount <= 1) {
    return 'new';
  }

  // Check if first visit was within last 24 hours
  const firstVisitTime = parseInt(firstVisit, 10);
  const hoursSinceFirst = (Date.now() - firstVisitTime) / (1000 * 60 * 60);

  if (hoursSinceFirst < 24) {
    return 'new';
  }

  return 'returning';
};

// Get session info for events
const getSessionInfo = () => {
  const sessionCount = parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '1', 10);
  return {
    user_type: getUserType(),
    session_number: sessionCount
  };
};

// ==========================================
// User Identification
// ==========================================

// Set user ID and properties for logged-in users
export const identifyUser = (user) => {
  if (!isGAAvailable() || !user) return;

  // Set user ID for cross-session tracking. A bare `config` call sends a page_view by default,
  // so it must be suppressed here or every login/session restore counts as an extra page view.
  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: String(user.id),
    send_page_view: false
  });

  // Set user properties
  window.gtag('set', 'user_properties', {
    subscription_status: user.subscription_status || 'free',
    is_pro_user: user.is_pro_user || false,
    email_verified: user.email_verified || false,
    user_type: getUserType()
  });
};

// Clear user identification on logout
export const clearUserIdentity = () => {
  if (!isGAAvailable()) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: null,
    send_page_view: false
  });

  window.gtag('set', 'user_properties', {
    subscription_status: null,
    is_pro_user: null,
    email_verified: null
  });
};

// ==========================================
// Page View Tracking
// ==========================================

// Track page views with enhanced context. This is the only source of page_view events (the
// automatic one is disabled in index.html). GA4 reports on page_location/page_title — the old
// `config` + `page_path` form was a Universal Analytics pattern. page_location keeps the query
// string so GA4 can read utm_* parameters on the landing page.
export const trackPageView = (path, title) => {
  if (!isGAAvailable()) return;

  trackEvent('page_view', {
    page_location: window.location.href,
    page_path: path,
    page_title: title
  });
};

// ==========================================
// Event Tracking
// ==========================================

// Track custom events with automatic context
export const trackEvent = (eventName, parameters = {}) => {
  if (!isGAAvailable()) return;

  const trafficSource = getTrafficSource();
  const sessionInfo = getSessionInfo();

  window.gtag('event', eventName, {
    ...parameters,
    ...trafficSource,
    ...sessionInfo
  });
};

// ==========================================
// Pre-defined Event Trackers
// ==========================================

export const analytics = {
  // ========== User Authentication Events ==========

  // Track signup with subscription context
  signUp: (subscriptionStatus = 'free') => trackEvent('sign_up', {
    method: 'email',
    subscription_status: subscriptionStatus,
    is_paid_signup: subscriptionStatus !== 'free'
  }),

  // Track login with user context
  login: (user = {}) => trackEvent('login', {
    method: 'email',
    subscription_status: user.subscription_status || 'unknown',
    is_pro_user: user.is_pro_user || false,
    login_count: user.login_count || 1
  }),

  // Track email verification
  emailVerified: () => trackEvent('email_verified'),

  // ========== Onboarding Events ==========

  // Track onboarding tour completion
  onboardingCompleted: (stepsViewed = 6) => trackEvent('onboarding_completed', {
    steps_viewed: stepsViewed,
    completion_type: 'finished'
  }),

  // Track onboarding tour skip
  onboardingSkipped: (stepSkippedAt = 0) => trackEvent('onboarding_skipped', {
    step_skipped_at: stepSkippedAt,
    completion_type: 'skipped'
  }),

  // Track onboarding step navigation
  onboardingStepViewed: (stepId, stepNumber) => trackEvent('onboarding_step_viewed', {
    step_id: stepId,
    step_number: stepNumber
  }),

  // ========== Trade Events ==========

  tradeCreated: (tradeType) => trackEvent('trade_created', {
    trade_type: tradeType
  }),

  tradeClosed: (tradeType) => trackEvent('trade_closed', {
    trade_type: tradeType
  }),

  tradeDeleted: () => trackEvent('trade_deleted'),

  // ========== Import/Export Events ==========

  tradesImported: (count, brokerage = 'native') => trackEvent('trades_imported', {
    trade_count: count,
    brokerage: brokerage
  }),

  tradesExported: (format) => trackEvent('trades_exported', {
    format: format
  }),

  // ========== Subscription Events ==========

  // Track subscription upgrade initiation (clicked upgrade button)
  subscriptionUpgradeInitiated: () => trackEvent('subscription_upgrade_initiated', {
    from_plan: 'free'
  }),

  // Track successful subscription start. Also sends GA4's standard `purchase` event so revenue
  // shows in Monetization reports; GA4 de-duplicates purchases by transaction_id (the Stripe
  // checkout session ID), so a reload of the success URL is not counted twice.
  subscriptionStarted: (plan = 'pro', transactionId = null) => {
    trackEvent('subscription_started', {
      plan: plan,
      value: 4.99,
      currency: 'USD'
    });
    trackEvent('purchase', {
      ...(transactionId ? { transaction_id: transactionId } : {}),
      value: 4.99,
      currency: 'USD',
      items: [{ item_id: plan, item_name: `Options Tracker ${plan}`, price: 4.99, quantity: 1 }]
    });
  },

  // Track subscription cancellation
  subscriptionCancelled: (reason = null) => trackEvent('subscription_cancelled', {
    cancellation_reason: reason
  }),

  // Track subscription reactivation
  subscriptionReactivated: () => trackEvent('subscription_reactivated'),

  // ========== Account Events ==========

  accountCreated: (accountType) => trackEvent('account_created', {
    account_type: accountType
  }),

  // ========== Feature Usage Events ==========

  featureUsed: (featureName, details = {}) => trackEvent('feature_used', {
    feature: featureName,
    ...details
  }),

  // Track specific feature usage
  calendarViewed: () => trackEvent('feature_used', { feature: 'calendar' }),
  dashboardViewed: () => trackEvent('feature_used', { feature: 'dashboard' }),
  toolsViewed: () => trackEvent('feature_used', { feature: 'tools' }),
  whatIfUsed: (symbol) => trackEvent('feature_used', { feature: 'what_if_scenarios', symbol }),

  // ========== Navigation & Exit Events ==========

  // Outbound link clicks are not tracked here: GA4 Enhanced Measurement records them as `click`.

  // Track CTA button clicks
  ctaClicked: (ctaName, location) => trackEvent('cta_clicked', {
    cta_name: ctaName,
    cta_location: location
  }),

  // Track pricing page views with context
  pricingViewed: (source) => trackEvent('pricing_viewed', {
    source: source // previous in-app path, or 'external' for a landing on /pricing
  }),

  // ========== Free Tools ==========

  // Track a successful calculation on a public calculator page
  calculatorUsed: (calculator, details = {}) => trackEvent('calculator_used', {
    calculator: calculator,
    ...details
  })
};

// ==========================================
// CTA Click Tracking
// ==========================================

// Paths whose links count as conversion CTAs. Tracked with one delegated listener instead of an
// onClick on every <Link>, so links inside blog post HTML (dangerouslySetInnerHTML) count too.
const CTA_PATHS = {
  '/register': 'signup',
  '/pricing': 'pricing',
  '/calculators': 'calculators'
};

const getCtaName = (pathname) => {
  if (CTA_PATHS[pathname]) return CTA_PATHS[pathname];
  if (pathname.startsWith('/calculators/')) return `calculator:${pathname.slice('/calculators/'.length)}`;
  return null;
};

// Returns a cleanup function. Ignores links to other origins and links to the current page.
export const initCtaTracking = () => {
  if (typeof document === 'undefined') return () => {};

  const handleClick = (event) => {
    const anchor = event.target.closest && event.target.closest('a[href]');
    if (!anchor) return;

    let url;
    try {
      url = new URL(anchor.getAttribute('href'), window.location.href);
    } catch (e) {
      return;
    }
    if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;

    const ctaName = getCtaName(url.pathname);
    if (!ctaName) return;

    analytics.ctaClicked(ctaName, window.location.pathname);
  };

  document.addEventListener('click', handleClick, true);
  return () => document.removeEventListener('click', handleClick, true);
};

// ==========================================
// Initialize on Load
// ==========================================

// Auto-initialize when module loads
if (typeof window !== 'undefined') {
  // Capture UTM params on first load
  captureUtmParams();
  // Initialize session tracking
  initSessionTracking();
}

export default analytics;
